import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Button, Chip, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { useIsCloudAdmin } from '../context/PermissionsContext';
import { Stat, StatGrid, errText, limit, limitGb } from '../components/ReportingUsageParts';

const EMPTY = { name: '', description: '', sample_limit: '', test_limit: '', storage_limit_gb: '' };
const toLimit = (v) => (String(v).trim() === '' ? null : Number(v));

function BundleDialog({ open, bundle, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(bundle ? { name: bundle.name, description: bundle.description || '', sample_limit: bundle.sample_limit ?? '', test_limit: bundle.test_limit ?? '', storage_limit_gb: bundle.storage_limit_gb ?? '' } : EMPTY);
  }, [open, bundle]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setError('');
    const body = { name: form.name.trim(), description: form.description.trim() || null, sample_limit: toLimit(form.sample_limit), test_limit: toLimit(form.test_limit), storage_limit_gb: toLimit(form.storage_limit_gb) };
    try {
      const res = bundle ? await reportingUsageAPI.updateBundle(bundle.id, body) : await reportingUsageAPI.createBundle(body);
      onSaved(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? 'Check the values: limits must be whole numbers of zero or more' : errText(err, 'Could not save the bundle'));
    } finally { setSaving(false); }
  };

  const limitField = (k, label, step) => (
    <TextField label={label} type="number" size="small" value={form[k]} onChange={set(k)} helperText="Leave blank for unlimited"
      slotProps={{ htmlInput: { min: 0, step }, inputLabel: { shrink: true } }} />
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{bundle ? 'Edit bundle' : 'New bundle'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Name" size="small" required value={form.name} onChange={set('name')} placeholder="e.g. Growth" />
        <TextField label="Description" size="small" value={form.description} onChange={set('description')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          {limitField('sample_limit', 'Sample limit', 1)}
          {limitField('test_limit', 'Test limit', 1)}
          {limitField('storage_limit_gb', 'Storage limit (GB)', 'any')}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!form.name.trim() || saving}>{saving ? 'Saving...' : 'Save bundle'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function BundleCard({ bundle, canEdit, busy, onEdit, onToggle }) {
  const inactive = !bundle.is_active;
  return (
    <Card sx={{ opacity: inactive ? 0.7 : 1 }}>
      <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{bundle.name}</Typography>
            {inactive && <Chip size="small" label="Inactive" />}
          </Box>
          {bundle.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{bundle.description}</Typography>}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" color="primary" variant="outlined" label={`Samples: ${limit(bundle.sample_limit)}`} />
            <Chip size="small" color="success" variant="outlined" label={`Tests: ${limit(bundle.test_limit)}`} />
            <Chip size="small" color="secondary" variant="outlined" label={`Storage: ${limitGb(bundle.storage_limit_gb)}`} />
          </Box>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!inactive && <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(bundle)} aria-label={`Edit ${bundle.name}`}>Edit</Button>}
            <Button size="small" color={inactive ? 'success' : 'error'} disabled={busy} onClick={() => onToggle(bundle)} aria-label={`${inactive ? 'Reactivate' : 'Deactivate'} ${bundle.name}`}>
              {inactive ? 'Reactivate' : 'Deactivate'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// Licence bundles that can be given to customers. Anyone can read them; Cloud Services administrators add, edit and retire them (bundles are never deleted).
export default function ReportingBundles() {
  const canEdit = useIsCloudAdmin();
  const [bundles, setBundles] = useState(null);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialog, setDialog] = useState({ open: false, bundle: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    reportingUsageAPI.bundles(false)
      .then((res) => { setBundles(res.data || []); setError(''); })
      .catch((err) => { setBundles([]); setError(errText(err, 'Could not load bundles')); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (b) => {
    setBusy(true); setError('');
    try { await reportingUsageAPI.updateBundle(b.id, { is_active: !b.is_active }); load(); }
    catch (err) { setError(errText(err, 'Could not update the bundle')); }
    finally { setBusy(false); }
  };

  const active = (bundles || []).filter((b) => b.is_active);
  const inactive = (bundles || []).filter((b) => !b.is_active);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Bundles</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Licence bundles define the sample, test and storage limits a customer can be given. Retired bundles stay on record because customers may still reference them.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!bundles && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {bundles && (
        <>
          <StatGrid>
            <Stat label="Active bundles" value={active.length} sub="available for assignment" />
            <Stat label="Inactive bundles" value={inactive.length} sub="hidden from assignment" />
            <Stat label="All bundles" value={bundles.length} />
          </StatGrid>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            {canEdit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, bundle: null })}>Add bundle</Button>}
            {inactive.length > 0 && (
              <FormControlLabel control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />} label={`Show ${inactive.length} inactive`} />
            )}
            {!canEdit && <Typography variant="caption" color="text.secondary">Only Cloud Services administrators can change bundles.</Typography>}
          </Box>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {active.map((b) => <BundleCard key={b.id} bundle={b} canEdit={canEdit} busy={busy} onEdit={(x) => setDialog({ open: true, bundle: x })} onToggle={toggle} />)}
            {active.length === 0 && <Paper sx={{ p: 2 }}><Typography color="text.secondary">No active bundles.{canEdit ? ' Add one above.' : ''}</Typography></Paper>}
            {showInactive && inactive.map((b) => <BundleCard key={b.id} bundle={b} canEdit={canEdit} busy={busy} onEdit={() => {}} onToggle={toggle} />)}
          </Box>
        </>
      )}
      <BundleDialog open={dialog.open} bundle={dialog.bundle} onClose={() => setDialog({ open: false, bundle: null })} onSaved={() => { setDialog({ open: false, bundle: null }); load(); }} />
    </Box>
  );
}
